import { gql } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Select, Switch, TableCell, TableRow, TextField } from "@mui/material";
import { capitalize, omit } from "lodash";
import { useState } from "react";
import { SupplyChangeReasonEventTypeEnum, useCreateSupplyChangeReasonMutation, useUpdateSupplyChangeReasonMutation } from "~/graphql/__generated__/graphql";
import { OrgConfig } from "~/providers/OrgConfigProvider";

type SupplyChangeReason = NonNullable<OrgConfig["supplyChangeReasons"]["string"]>

interface Props {
  reason: SupplyChangeReason
}

const SupplyChangeReasonRow = ({ reason }: Props) => {
  const [active, setActive] = useState(reason.active)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [update, { loading }] = useUpdateSupplyChangeReasonMutation();

  return <TableRow>
    <TableCell sx={{ width: '10%', maxWidth: '120px' }}>
      {capitalize(reason.eventType)}
    </TableCell>
    <TableCell
      sx={{
        width: '70%',
        maxWidth: '500px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span>{reason.label || 'No label'}</span>
        <IconButton
          size="small"
          onClick={() => setDialogOpen(true)}
          sx={{ minWidth: 'auto', padding: '4px' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </div>
    </TableCell>
    <TableCell sx={{ width: '15%', maxWidth: '100px' }}>
      <Switch checked={active} disabled={loading} onChange={() => {
        setActive(!active)
        update({ variables: { ...reason, active: !active }})
      }} size="small" />
    </TableCell>

    {dialogOpen && <SupplyChangeReasonDialog onClose={() => setDialogOpen(false)} reason={reason} />}
  </TableRow>
}

export default SupplyChangeReasonRow;


interface SupplyChangeReasonDialogProps {
  onClose: () => void;
  reason: { id: number, label: string, active: boolean, eventType: SupplyChangeReasonEventTypeEnum };
}

export const SupplyChangeReasonDialog = ({
  onClose,
  reason,
}: SupplyChangeReasonDialogProps) => {
  const [local, setLocal] = useState(reason);
  const [create, { loading: createLoading }] = useCreateSupplyChangeReasonMutation();
  const [update, { loading: updateLoading }] = useUpdateSupplyChangeReasonMutation();

  const loading = createLoading || updateLoading

  const handleSave = async () => {
    if (local.id) {
      await update({ variables: omit(local, "eventType") })
    } else {
      await create({
        variables: omit(local, "id"),
        awaitRefetchQueries: true,
        refetchQueries: ["OrgConfig"],
      })
    }

    onClose();
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {reason ? 'Edit Supply Change Reason' : 'Add Supply Change Reason'}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Label"
          fullWidth
          variant="outlined"
          value={local.label}
          onChange={(e) => setLocal({ ...local, label: e.target.value })}
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <Select
          margin="dense"
          fullWidth
          value={local.eventType}
          onChange={(e) => setLocal({ ...local, eventType: e.target.value as SupplyChangeReasonEventTypeEnum })}
          disabled={!!local.id || loading}
          sx={{ mb: 2 }}
        >
          <MenuItem value="deposit">Deposit (adding to stock)</MenuItem>
          <MenuItem value="withdrawal">Withdrawal (removing from stock)</MenuItem>
          <MenuItem value="recount">Recount</MenuItem>
        </Select>
        <FormControlLabel
          control={
            <Switch
              checked={local.active}
              onChange={(e) => setLocal({ ...local, active: e.target.checked })}
              disabled={loading}
            />
          }
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          loading={loading}
          disabled={!local.label?.trim()}
        >
          {loading ? (reason ? 'Updating...' : 'Creating...') : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

gql`
  mutation CreateSupplyChangeReason(
    $label: String!
    $eventType: SupplyChangeReasonEventTypeEnum!
  ) @permission(requires: manageOrgConfig) {
    createSupplyChangeReason(label: $label, eventType: $eventType) {
      id
    }
  }
`;

gql`
  mutation UpdateSupplyChangeReason(
    $id: Int!
    $label: String!
    $active: Boolean!
  ) @permission(requires: manageOrgConfig) {
    updateSupplyChangeReason(
      id: $id
      label: $label
      active: $active
    ) {
      id
      label
      active
    }
  }
`;
